import {Navigate, RouteObject} from 'react-router';

export const servicesRoutes: RouteObject[] = [
  // ── The split ─────────────────────────────────────────────────────────
  // /services used to carry student tools, assessments, the free CV builder,
  // paid services AND packages on one page. Each is now its own destination,
  // because a visitor could not tell what they would be operating from what
  // they would be buying.
  {
    path: 'tools',
    lazy: () => import('@app/catalog/tools-page'),
  },
  {
    path: 'assessments',
    lazy: () => import('@app/catalog/assessments-page'),
  },
  {
    // Literal segment first, so it wins over the :slug route below.
    path: 'assessments/english-placement',
    lazy: () => import('@app/services/exam/placement-exam-page'),
  },
  {
    path: 'assessments/:slug',
    lazy: () => import('@app/catalog/assessment-runner-route'),
  },
  {
    // Before 'services', so the literal path is not swallowed by it.
    path: 'services/packages',
    lazy: () => import('@app/services/service-packages-page'),
  },
  {
    // A package's own page. Three segments, so it cannot be swallowed by
    // 'services/:serviceSlug' (two) or by 'services/packages' (two).
    path: 'services/packages/:packageSlug',
    lazy: () => import('@app/services/service-package-detail-page'),
  },
  {
    path: 'services',
    lazy: () => import('@app/services/services-catalog-page'),
  },
  {
    path: 'services/pricing',
    lazy: () => import('@app/services/pricing-page'),
  },
  {
    // Moved to /assessments/english-placement. Kept as a redirect: this URL is
    // in sent emails, shared links and search results.
    path: 'services/placement-exam',
    element: <Navigate to="/assessments/english-placement" replace />,
  },
  {
    // Student-facing "my exams" history page. Must come BEFORE the
    // dynamic :examSlug route so the literal "my-exams" segment wins.
    path: 'services/placement-exam/my-exams',
    lazy: () => import('@app/services/exam/my-exams-page'),
  },
  {
    // Direct-link to a specific placement exam by slug — share/email
    // links open the catalog with that exam pre-selected. Lets us add
    // more exams later (other languages, EF v2, IELTS-style, …) each
    // with its own permalink.
    path: 'services/placement-exam/:examSlug',
    lazy: () => import('@app/services/exam/placement-exam-page'),
  },
  {
    // Runner for personality / career assessments (MBTI, RIASEC, etc.).
    path: 'services/personality-tests/:testId',
    lazy: () => import('@app/services/exam/personality-test-runner-page'),
  },
  {
    path: 'services/:serviceSlug/request',
    lazy: () => import('@app/services/service-request-page'),
  },
  {
    // LAST among the /services routes: a slug pattern would otherwise swallow
    // `packages`, `pricing` and `placement-exam` above it.
    path: 'services/:serviceSlug',
    lazy: () => import('@app/services/service-detail-page'),
  },
  {
    path: 'my-payments',
    lazy: () => import('@app/services/my-payments-page'),
  },
  {
    path: 'checkout',
    lazy: () => import('@app/checkout/checkout-page'),
  },
  {
    path: 'forms/:slug',
    lazy: () => import('@app/wizards/wizard-renderer'),
  },
  {
    // Public volunteer programmes. Readable without an account: somebody
    // deciding whether to give us their time should see what they would be
    // agreeing to before they agree.
    path: 'volunteer',
    lazy: () => import('@app/volunteers/public/volunteer-programs-page'),
  },
  {
    path: 'volunteer/:slug',
    lazy: () => import('@app/volunteers/public/volunteer-program-page'),
  },
  {
    // Certificate verification. Public and unauthenticated — an employer
    // checking a certificate has no account here and should not need one.
    path: 'certificates/verify/:code',
    lazy: () => import('@app/volunteers/public/certificate-verify-page'),
  },
  {
    path: 'certificates/verify',
    lazy: () => import('@app/volunteers/public/certificate-verify-page'),
  },
  {
    // The volunteer application lives in the portal now, on the page that
    // already knows whether you have applied. This route pointed at a form
    // wizard that was never created, so it said "Wizard not found" to
    // everybody — kept as a redirect because it is the URL that has been
    // linked from elsewhere.
    path: 'volunteer-registration',
    element: <Navigate to="/student/volunteer" replace />,
  },
  {
    path: 'scholarship-application',
    lazy: () => import('@app/wizards/wizard-renderer'),
  },
  {
    path: 'client-onboarding',
    lazy: () => import('@app/wizards/wizard-renderer'),
  },
  {
    path: 'university-application',
    lazy: () => import('@app/wizards/wizard-renderer'),
  },
  {
    path: 'contact-feedback',
    lazy: () => import('@app/wizards/wizard-renderer'),
  },
];
